import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { parseHumanReadable } from 'cron-js-parser';
import * as jsonpath from 'jsonpath';
import { isEmpty, isString } from 'lodash';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EMPTY_NUMBER_VALUE } from 'src/app/common/const';
import { gsm, wifi } from 'src/app/ds/component/device/device-selector/device-selector.component';
import { StopPileStatEventType } from 'src/app/reports/model/stop-pile-stat-event-type';
import { CellType } from 'src/app/settings/model/cell-type';
import { FieldsService } from 'src/app/settings/service/fields.service';
import { UserSettingsService } from 'src/app/settings/service/user-settings.service';
import { DeviceType } from 'src/app/vehicles/model/device-type';
import { IrmaStatusCode } from 'src/app/vehicles/model/live-data-report/irma-status-code';
import { CAN1Status } from 'src/app/vehicles/model/status/can1-status';
import { LcdStatus } from 'src/app/vehicles/model/status/lcd-status';
import { VehicleLayoutType } from 'src/app/vehicles/model/vehicle-layout-type';
import { FieldType } from '../../settings/model/field-type';
import { ValueField } from '../../settings/model/value-field';
import { toVisibleDateFormat, toVisibleDateTimeFormat } from './date-util';
import { ColumnDefinition } from '../model/column-definition';
import { DataSource, DataSourceField } from '../model/data-source';
import { BytesHumanReadablePipe } from '../pipe/bytes-human-readable.pipe';
import { LocationTypePipe } from '../pipe/location-type-pipe';
import { TableColumnsService } from '../service/table-columns.service';

@Injectable({
  providedIn: 'root'
})
export class DataSourceCreator {

  // Kolumny tabeli Pojazdy (mapa) z wyborem wartości z listy w filtrze kolumnowym. Ramki
  // z websocketu leżą w całości w pamięci, więc lista pokrywa wszystkie pojazdy - tabele
  // stronicowane po serwerze znałyby tylko wczytaną stronę.
  private static readonly GPS_FRAME_VALUE_LIST_COLUMNS = [
    'veh_side_number', 'line', 'team', 'carrier', 'network_address', 'direction_name'
  ];

  irmaStatusCode: IrmaStatusCode = new IrmaStatusCode();

  constructor(private translate: TranslateService, private fieldService: FieldsService, private tableColumns: TableColumnsService,
    private userSetings: UserSettingsService) { }

  createForDeviceType(deviceType: DeviceType, name: string | null = null): Observable<DataSource> {
    return this.fieldService.getFieldsForType(deviceType)
      .pipe(map(fields => this.create(name || deviceType, fields)));
  }

  create(name: any, fields: ValueField[]): DataSource {
    const dataSourceFields: DataSourceField[] = [];

    const columnsSettings = this.tableColumns.getDataTableColumnsSettings(name);

    fields.forEach(valueField => {
      const column = this.createColumn(valueField);
      const columnSettings = columnsSettings[column.name];
      if ((columnSettings && !(columnSettings.visible ?? valueField.visible)) ||
        (!columnSettings && (valueField.defaultHidden || valueField.permanentlyHidden))) {
        column.visible = false;
        column.translatable = true;
      }

      if (column.formatterParams && !valueField.options) {
        const options = Object.keys(column.formatterParams).filter(key => key != 'jsonpath' && key != 'format');
        if (options.length > 0) {
          valueField.options = options.map(key => ({
            key,
            value: column.formatterParams[key]
          }));
        }
      }
      dataSourceFields.push({
        column,
        valueField
      });
    });

    const column = Object.assign(new ColumnDefinition(), { name: 'rowId', visible: false });
    const valueField = Object.assign(new ValueField(), { notDevice: true, visible: false });
    dataSourceFields.push({ column, valueField });
    return new DataSource(name, dataSourceFields);
  }

  private createColumn(field: ValueField): ColumnDefinition {
    const testerDiskSpace = new RegExp('/*diskspace/*');
    const column = new ColumnDefinition();
    column.name = field.fieldName || field.name;
    column.title = field.title || column.name;
    column.visible = field.visible;
    column.style = field.style;

    if (field.groupingHeaderName)
      column.groupingHeaderName = field.groupingHeaderName;

    if (field.jsonpath) {
      if (field.type == FieldType.ARRAY)
        column.mutator = (data: any, params?: any) => jsonpath.query(data, params?.jsonpath);
      else
        column.mutator = (data: any, params?: any) => jsonpath.value(data, params?.jsonpath);
      column.mutatorParams = { jsonpath: field.jsonpath };
    }

    if (field.type == FieldType.BOOLEAN) {
      column.formatter = (value?: any) => {
        if (value === true || value === 1 || value === '1' || value === 'true')
          return this.translate.instant('yes');
        else if (value === false || value === 0 || value === '0' || value === 'false')
          return this.translate.instant('no');
      };
      field.groupingValueFormat = (value) => column.formatter(value);
    }

    if (field.type == FieldType.VIOLATIONS_MARKER) {
      field.cellType = CellType.VIOLATIONS_MARKER;
    }
    if (field.type == FieldType.VIOLATIONS_MARKER_2) {
      field.cellType = CellType.VIOLATIONS_MARKER_2;
    }

    if (field.name == wifi || field.name == gsm) {
      column.formatter = () => '';
    }

    if (field.conversion != null && (field.type == FieldType.INTEGER || field.type == FieldType.FLOAT)) {
      const tester = new RegExp(/([*\/+-])(\d+\.\d+|\d+)/);
      if (tester.test(field.conversion)) {
        column.formatter = (value?: any, params?: any) => {
          if (value != null) {
            const converted = this.convertValue(value, params.conversion);
            if (params.format)
              return ColumnDefinition.formatDecimal(converted, params.format);
            else
              return converted;
          }
        };
        column.formatterParams = { conversion: field.conversion };
      }
    }

    // Wartości będące już Date idą szybką ścieżką bez moment; stringi/liczby zachowują
    // dotychczasową semantykę parsowania moment(value).
    if (field.type == FieldType.DATETIME) {
      column.formatter = (value?: any) => {
        if (value)
          return value instanceof Date ? toVisibleDateTimeFormat(value) : moment(value).toVisibleDateTimeFormat();
      };
      field.groupingValueFormat = (value) => column.formatter(value);
    }

    if (field.type == FieldType.DATE) {
      column.formatter = (value?: any) => {
        if (value)
          return value instanceof Date ? toVisibleDateFormat(value) : moment(value).toVisibleDateFormat();
      };
      field.groupingValueFormat = (value) => column.formatter(value);
    }

    if (field.type == FieldType.DURATION) {
      column.formatter = (value?: any) => {
        if (value != null) {
          const absolute = Math.abs(value);
          const timeStamp = moment.utc(moment.duration(absolute, 'seconds').asMilliseconds()).toTime(true);
          if (field.name === 'timetable_deviation' || field.name === 'timetableDeviation') {
            return this.userSetings.isReverseTimetableDeviationEnabled() ? (value > 0 ? '-' : '+') + timeStamp : (value < 0 ? '-' : '+') + timeStamp;
          } else
            return (value < 0 ? '-' : '') + timeStamp;
        }
      };
    }

    if (field.type == FieldType.DEVICE_ID) {
      column.formatter = (value?: any) => {
        if (value != null) {
          return value.startsWith('::ffff:') ? value.replace('::ffff:', '') : value;
        }
      };
    }

    if (field.type === FieldType.SWITCH) {
      column.formatter = (value?: any) => {
        if (value === true || value === 1 || value === '1' || value === 'true')
          return this.translate.instant('switch_on');
        else if (value === false || value === 0 || value === '0' || value === 'false')
          return this.translate.instant('switch_off');
        else
          return this.translate.instant('not_supported');
      };
    }

    if (field.type == FieldType.AGID) {
      column.formatter = (value?: any) => {
        if (value != null) {
          return value.toString(16);
        }
      };
    }

    if (field.type == FieldType.GEAR) {
      column.formatter = (value?: any) => {
        if (value != null)
          return CAN1Status.getGearText(value);
        else
          return '-';
      };
    }

    if (field.type == FieldType.LCD_STATUSES) {
      column.formatter = (value?: any) => {
        if (value)
          return JSON.stringify(value);
      };
      column.sortable = false;
    }

    if (field.type == FieldType.LCD_VERSIONS) {
      column.formatter = (value?: any) => {
        if (value && value instanceof LcdStatus)
          return value.getVersions();
      };
      column.sortable = false;
    }

    if (field.type == FieldType.EVENT_TYPE) {
      column.formatter = ColumnDefinition.numberTypeFormatter;
      column.formatterParams = {
        1: 'doors_opened',
        2: 'doors_closed'
      };
      column.translatable = true;
      field.groupingValueTranslatable = true;
      field.groupingValueFormat = (value) => !isEmpty(value) ? column.formatterParams[value] : value;
    }

    if (field.type === FieldType.STOP_PILE_EVENT_TYPE_RAW || field.type === FieldType.STOP_PILE_EVENT_TYPE_STAT) {
      column.formatter = (value) => !isEmpty(value) ? StopPileStatEventType[value] : value;
      column.translatable = true;
      field.groupingValueTranslatable = true;
      field.groupingValueFormat = (value) => !isEmpty(value) ? StopPileStatEventType[value] : value;
    }

    if (field.type == FieldType.IRMA) {
      column.formatter = ColumnDefinition.numberTypeFormatter;
      column.formatterParams = this.irmaStatusCode.getOpionsForFieldIrma();
      column.translatable = true;
    }

    if (field.type === FieldType.IRMA_CODE_DESCRIPTION) {
      column.formatter = ColumnDefinition.numberTypeFormatter;
      column.formatterParams = this.irmaStatusCode.getOpionsForFieldIrmaCodeDescription();
      column.translatable = true;
    }

    if (field.type === FieldType.IRMA_STATUS_CODE) {
      column.formatter = ColumnDefinition.numberTypeFormatter;
      column.formatterParams = this.irmaStatusCode.getOpionsForFieldIrmaStatusCode();
      column.translatable = true;
    }

    if (field.type == FieldType.IRMA_NUMBER) {
      column.formatter = ColumnDefinition.numberTypeFormatter;
      column.formatterParams = {
        1: 'Irma-1',
        2: 'Irma-2',
        3: 'Irma-3',
        4: 'Irma-4',
        5: 'Irma-5',
        6: 'Irma-6'
      };
      field.groupingValueFormat = (value) => !isEmpty(value) ? column.formatterParams[value] : value;
    }

    if (field.type == FieldType.CONST_VALUE) {
      column.formatter = (value: any) => {
        if (!isEmpty(field.constValue))
          return field.constValue;
        else
          return value;
      };
    }

    if (field.name === 'syncTime' && field.type == FieldType.DATETIME) {
      column.formatter = (value?: any) => {
        if (value != null)
          return value instanceof Date ? toVisibleDateTimeFormat(value) : moment(value).toVisibleDateTimeFormat();
        else
          return this.translate.instant('lackOfData');
      };
    }

    if (!column.formatter) {

      if (field.type == FieldType.CRON) {
        column.formatter = (value: any) => {
          if (isString(value))
            return parseHumanReadable(value, null, this.translate.currentLang);
          return value;
        };
        column.translatable = false;
      }

      if (field.name == 'bus_fill' || field.name == 'bus_fill_corrected' || field.name === 'busFill') {
        column.formatter = (value?: any) => {
          if (value != null)
            return (+value).toFixed(2) + '%';
        };
      }

      if (field.name == 'route_id') {
        column.formatter = (value?: any) => {
          if (value != EMPTY_NUMBER_VALUE)
            return value;
        };
      }

      if (field.name == 'agid') {
        column.formatter = (value?: any) => {
          if (value != 0)
            return value;
        };
      }

      if (field.name == 'route_control_state') {
        column.formatter = ColumnDefinition.numberTypeFormatter;
        column.formatterParams = {
          1: 'onRoute',
          2: 'outsideRoute'
        };
        column.translatable = true;
      }

      if (field.name == 'PropB_4B' || field.name == 'AMZ2' || field.name == 'AMZ6') {
        column.formatter = (value?: any) => {
          if (value && Array.isArray(value)) {
            const hexValues = [];
            value.forEach(element => {
              if (element != null && !isNaN(+element))
                hexValues.push('0x' + element.toString(16).toUpperCase());
              else
                hexValues.push(element);
            });
            return `[${hexValues.join(', ')}]`;
          } else {
            return value;
          }
        };
      }

      if (field.name == 'amz2_text') {
        column.formatter = (value?: any) => {
          if (value && Array.isArray(value)) {
            const results = [];
            value.forEach((element, byteIdx) => {
              if (element != null && !isNaN(+element)) {
                const bits = element.toString(2).split('').reverse();
                bits.forEach((bit, bitIdx) => {
                  if (bit == '1')
                    results.push(`AMZ2_${byteIdx + 1}_${bitIdx + 1}`);
                });
              }
            });
            return results;
          }
        };
        column.translatable = true;
        column.wrap = true;
      }

      if (field.name == 'locationType') {
        column.formatter = (value?: any) => {
          if (value)
            return new LocationTypePipe().transform(value).toLowerCase();
        };
        column.translatable = true;
      }
    }

    if (field.name == 'bus_stop_type') {
      column.formatter = ColumnDefinition.numberTypeFormatter;
      column.formatterParams = {
        0: 'start',
        1: 'middle',
        2: 'end'
      };
      column.translatable = true;
    }

    if (field.fieldName == 'vehicle_layout_type' || field.type == FieldType.VEHICLE_LAYOUT_TYPE) {
      field.options = Object.values(VehicleLayoutType).map(value => ({ value, key: value }));
      column.formatter = (v, _p) => v !== null && v !== undefined ? VehicleLayoutType[(v as string).toUpperCase()] : v;
      field.translatable = true;
      field.groupingValueTranslatable = true;
      field.groupingValueFormat = (value) => value !== null && value !== undefined ? VehicleLayoutType[(value as string).toUpperCase()] : value;
    }

    if (field.deviceType == DeviceType.GPS_FRAME && DataSourceCreator.GPS_FRAME_VALUE_LIST_COLUMNS.includes(field.name)) {
      field.filterOptionsFromData = true;
      // Nr boczny ma każda ramka, pozostałych wartości mieć nie musi.
      if (field.name != 'veh_side_number')
        field.filterEmptyOptionLabel = 'column-filter.no-values';
    }

    if (testerDiskSpace.test(field.name.toLowerCase())) {
      column.formatter = (value: number) => new BytesHumanReadablePipe().transform(value, 'kB');
    }

    if (field.cellType == CellType.JSON_OBJECT) {
      if (field.jsonpath) {
        column.mutator = (data: any, params?: any) => jsonpath.value(data, params?.jsonpath);
        column.mutatorParams = { jsonpath: field.jsonpath };
      }
      column.formatter = () => '';
      column.translatable = false;
      column.formatterParams = {};
      column.formatterParams.cellType = field.cellType;
    }

    if (field.cellType == CellType.VIOLATIONS_MARKER) {
      column.formatter = () => '';
      column.translatable = false;
      column.formatterParams = {};
      column.formatterParams.cellType = CellType.VIOLATIONS_MARKER;
    }

    if (field.cellType == CellType.VIOLATIONS_MARKER_2) {
      column.formatter = () => '';
      column.translatable = false;
      column.formatterParams = {};
      column.formatterParams.cellType = CellType.VIOLATIONS_MARKER_2;
    }

    if (field.cellType == CellType.ICON_MENU) {
      column.formatter = () => '';
      column.translatable = false;
      column.formatterParams = {};
      column.formatterParams.cellType = field.cellType;
      column.formatterParams.iconMenu = field.options;
    }

    if (field.cellType == CellType.FILL_BAR) {
      column.formatter = () => '';
      column.translatable = false;
      column.formatterParams = {};
      column.formatterParams.cellType = field.cellType;
      column.formatterParams.fillNegative = field.options;
    }

    if (field.cellType == CellType.ICON) {
      column.formatter = () => '';
      column.translatable = false;
      column.formatterParams = {};
      column.formatterParams.cellType = field.cellType;
      column.formatterParams.icon = field.options;
    }

    if (!column.formatter && field.options) {
      column.formatter = ColumnDefinition.typeFormatter;
      column.formatterParams = {};
      field.options.forEach(option => column.formatterParams[option.key] = option.value);
      column.translatable = true;
      field.groupingValueTranslatable = true;
      field.groupingValueFormat = (value) => ColumnDefinition.typeFormatter(value, column.formatterParams);
    }

    if (field.format) {
      if (!column.formatterParams)
        column.formatterParams = {};
      column.formatterParams.format = field.format;
    }

    if (field.translatable !== undefined) column.translatable = field.translatable;


    if (field.disableSort) {
      column.sortable = false;
    }

    return column;
  }

  private convertValue(value: any, conversion: string): any | null {
    if (value == null)
      return null;
    if (value == -1.0 || value == -1)
      return null;
    else
      return eval(value + conversion);
  }

}
